import qs from 'query-string'

import { SERIAL_TICKETS } from '../utils/mode'
import { updateParamsInUrl } from '../utils/Urls'

let params = {}
let handleError = null
const POST_SUMMARY = 'postSummary'
const requestNameToAbortControllers = {
  [POST_SUMMARY]: []
}

function init({ locale, errorHandler, url, paymentServiceUrl }) {
  const api = `${url}/api`
  const paymentServiceApi = `${paymentServiceUrl}/api`
  params = { locale, api, paymentServiceApi }
  handleError = errorHandler
}

function deactivate() {
  params = {}
  handleError = null
}

async function fetchWithErrorHandling(resource, init, errorHandler = handleError, throwGlobalError = true) {
  try {
    const response = await fetch(resource, init)

    // eslint-disable-next-line no-throw-literal
    if (response.status >= 400) throw { status: response.status, url: response.url }
    if (response.status === 204) return Promise.resolve()
    return response.json()
  } catch (error) {
    if (errorHandler) errorHandler(error)
    if (throwGlobalError) throw error
  }
}

function getClientBySsoToken(ssoToken, handleError) {
  return fetchWithErrorHandling(`${params.api}/sso/${ssoToken}/client`, undefined, handleError, false)
}

function getAvailableSerialTickets({ mode, ...queryParams }) {
  return fetchWithErrorHandling(
    `${params.api}/serial-tickets?${qs.stringify({ locale: adjustLocale(params.locale, mode), mode, ...queryParams })}`
  )
}

function getVoucherInfo(code) {
  return fetchWithErrorHandling(`${params.api}/vouchers/${code}`)
}

function getUserCoupons(queryParams) {
  return fetchWithErrorHandling(`${params.api}/coupons?${qs.stringify({ ...queryParams })}`)
}

function getReservation(token) {
  return fetchWithErrorHandling(`${params.api}/reservations/${token}?${qs.stringify({ locale: params.locale })}`)
}

function getCompany(companyId) {
  return fetchWithErrorHandling(`${params.api}/companies/${companyId}`)
}

function getAgent(agentId, companyId) {
  return fetchWithErrorHandling(`${params.api}/companies/${companyId}/agents/${agentId}`)
}

async function postSummary(postParams, mode) {
  try {
    const abortController = createAbortController(POST_SUMMARY)
    const response = await fetch(
      `${params.api}/reservations/summary?${qs.stringify({ locale: adjustLocale(params.locale, mode) })}`,
      {
        signal: abortController.signal,
        mode: 'cors',
        method: 'post',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8'
        },
        body: JSON.stringify(postParams)
      }
    )
    return response.json()
  } catch (ex) {
    if (ex.name === 'AbortError') return
    throw ex
  }
}

function abortPostSummary() {
  abortRequest(POST_SUMMARY)
}

function createAbortController(requestName) {
  abortRequest(requestName)
  const abortController = new AbortController()
  requestNameToAbortControllers[requestName].push(abortController)
  return abortController
}

function abortRequest(requestName) {
  const latestAbortController = requestNameToAbortControllers[requestName].pop()
  if (latestAbortController) latestAbortController.abort()
}

async function getMessages(response) {
  const SYSTEM_ERROR_CODE = -1
  try {
    const { errors } = await response.json()
    return errors.filter(({ code }) => code !== SYSTEM_ERROR_CODE).map(({ message }) => message)
  } catch (error) {
    return null
  }
}

async function confirmReservation(requestData, mode) {
  try {
    const response = await fetch(
      `${params.api}/reservations/confirm?${qs.stringify({ locale: adjustLocale(params.locale, mode) })}`,
      {
        mode: 'cors',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      }
    )

    if (response.status >= 400) {
      const messages = await getMessages(response)
      if (messages?.length > 0) return { error: true, messages }

      // eslint-disable-next-line no-throw-literal
      throw { status: response.status, url: response.url }
    }
    return response.json()
  } catch (error) {
    if (handleError) handleError(error)
    throw error
  }
}

function adjustLocale(locale, mode) {
  if (mode === SERIAL_TICKETS) {
    const UNSUPPORTED_MARKETS = ['SE', 'DE']
    const UNSUPPORTED_LANGUAGES_WITHOUT_MARKETS = ['sv', 'de']
    const [language, market] = locale.split('-')

    if (UNSUPPORTED_MARKETS.includes(market) || (!market && UNSUPPORTED_LANGUAGES_WITHOUT_MARKETS.includes(language))) {
      return `${language}-FI`
    }
  }

  return locale
}

function redirectToConfirmationPage(confirmResponse) {
  updateHistoryWithPaymentCancelledUrl()

  const hashParameters = { '/': '', afterChange: '' }
  const requestParameters = {
    ...qs.parseUrl(confirmResponse.url).query,
    ...confirmResponse.parameters
  }
  window.location.href = updateParamsInUrl(confirmResponse.url, requestParameters, hashParameters)
}

function redirectToPaymentServicePage(confirmResponse) {
  updateHistoryWithPaymentCancelledUrl()

  window.location.href = updateParamsInUrl(confirmResponse.url, confirmResponse.parameters)
}

function updateHistoryWithPaymentCancelledUrl() {
  const { location } = window
  const paymentCancelledUrl = updateParamsInUrl(location.href, {
    ...qs.parse(location.search),
    backendError: 'paymentCancelled'
  })
  window.history.replaceState({}, '', paymentCancelledUrl)
}

function getAvailablePaymentTypes(mode) {
  return fetchWithErrorHandling(
    `${params.api}/v4/payments?${qs.stringify({ locale: adjustLocale(params.locale, mode) })}`
  )
}

function createPdfLink(resToken) {
  return `${params.api}/reservations/${resToken}/pdf`
}

export default {
  abortPostSummary,
  adjustLocale,
  confirmReservation,
  createPdfLink,
  deactivate,
  getAgent,
  getAvailablePaymentTypes,
  getAvailableSerialTickets,
  getClientBySsoToken,
  getCompany,
  getReservation,
  getUserCoupons,
  getVoucherInfo,
  init,
  postSummary,
  redirectToConfirmationPage,
  redirectToPaymentServicePage
}
